import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { enqueueSnackbar } from "notistack"
import { Grid, MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { ShopsListResponse, ShopsQuery, deleteShop, fetchShopsList } from "../shopsApi"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { StandardChip } from "../../../../../shared/components/badges/StandardChip"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    MERCHANT_ADMIN_SHOPS_LIST_CREATE_ROUTE,
    MERCHANT_ADMIN_ROUTE,
    getEditShopRoute,
} from "../../../../../constants/routes"
import { Shop } from "../shopsTypes"
import { DestructiveMenuItem } from "../../../../../shared/components/menus/DestructiveMenuItem"

const defaultModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}
const selectShops = (data: ShopsListResponse) => data?.shops || []

export function Shops() {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>(defaultModalState)

    const shopsResult = useApiQuery(fetchShopsList, {
        queryName: ShopsQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectShops,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(shopsResult)

    const columns = useMemo<MRT_ColumnDef<Shop>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("shops-table-case-column"),
                accessorKey: "caseNumber",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("shops-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("shops-table-type-column"),
                accessorKey: "type",
            }),
            columnFactory.statusColumn({
                header: l10n.getString("shops-table-status-column"),
                accessorFn: (originalRow) =>
                    originalRow.isActive && originalRow.demoMode
                        ? "demo"
                        : originalRow.isActive && !originalRow.demoMode
                        ? "active"
                        : "inactive", // must be a string
                Cell: ({ row }) => {
                    if (row.original.isActive && row.original.demoMode) {
                        return (
                            <LocalizedStrict id="shops-table-status-column-demo">
                                <StandardChip variant="info" placement="table">
                                    Demo
                                </StandardChip>
                            </LocalizedStrict>
                        )
                    } else if (row.original.isActive && !row.original.demoMode) {
                        return (
                            <LocalizedStrict id="shops-table-status-column-active">
                                <StandardChip variant="success" placement="table">
                                    Active
                                </StandardChip>
                            </LocalizedStrict>
                        )
                    } else {
                        return (
                            <LocalizedStrict id="shops-table-status-column-inactive">
                                <StandardChip variant="red" placement="table">
                                    Inactive
                                </StandardChip>
                            </LocalizedStrict>
                        )
                    }
                },
                filterSelectOptions: [
                    {
                        value: "active",
                        text: l10n.getString("shops-table-status-column-active-filter-active"),
                    },
                    {
                        value: "inactive",
                        text: l10n.getString("shops-table-status-column-inactive-filter-inactive"),
                    },
                    {
                        value: "demo",
                        text: l10n.getString("shops-table-status-column-demo-filter-demo"),
                    },
                ],
            }),
        ],
        [columnFactory, l10n],
    )

    const { mutate: callDeleteShop } = useApiMutation(deleteShop, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ShopsQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteShop({ shopId: confirmModalState.itemId })
        } else {
            throw new Error("Shop id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteShop])

    return (
        <>
            <LocalizedStrict id="shops-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Shops"
                    page={Page.MERCHANT_ADMIN_SHOPS}
                    rightElement={
                        <LocalizedStrict id="shops-table-create-button">
                            <StandardButton onClick={() => navigate(MERCHANT_ADMIN_SHOPS_LIST_CREATE_ROUTE)}>
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-shops">
                            <Breadcrumb>Shops</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-shops">
                            <Breadcrumb>Shops</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="shops-table-menu-item-edit">
                            <MenuItem
                                onClick={() => {
                                    navigate(getEditShopRoute(row.original.id))
                                }}
                                key={`edit-${row.original.id}`}
                            >
                                Edit
                            </MenuItem>
                        </LocalizedStrict>,
                        <LocalizedStrict id="shops-table-menu-item-delete">
                            <DestructiveMenuItem
                                onClick={() => {
                                    setConfirmModalState({
                                        showConfirmModal: true,
                                        confirmModalText: `${row.original.name}`,
                                        itemId: row.original.id,
                                    })
                                    closeMenu()
                                }}
                                key={`delete-${row.original.id}`}
                            >
                                Delete
                            </DestructiveMenuItem>
                        </LocalizedStrict>,
                    ]}
                />
            </Grid>
            {confirmModalState.showConfirmModal && (
                <LocalizedStrict id="shops-confirm-delete-shop-dialog" attrs={{ typeOfEntity: true }}>
                    <DeleteConfirmDialog
                        typeOfEntity="shop"
                        entityName={confirmModalState.confirmModalText}
                        handleClick={handleDeleteClick}
                        open={confirmModalState.showConfirmModal}
                        onClose={() => setConfirmModalState(defaultModalState)}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
